<template>
  <article
      :class="[
      'relative group rounded-2xl transition-all duration-200',
      theme.cardBg,
      signal.locked ? 'opacity-70' : ''
    ]"
  >
    <div class="p-4">
      <!-- Header -->
      <header class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="inline-flex items-center gap-1.5 text-xl font-semibold leading-6 truncate" :class="[theme.baseTextColor]">
            <span class="truncate">{{ signal.name }}</span>
          </div>

          <div class="mt-1 flex flex-wrap items-center gap-2">
            <TheTooltip :id="`${signal.id}_${signal.max_leverage}`" :message="$t('max_leverage')">
              <span class="inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded-full border bg-neutral-50/70 dark:bg-white/5 border-neutral-200 dark:border-white/10">
                x{{ signal.max_leverage }}
              </span>
            </TheTooltip>

            <TheTooltip :id="`${signal.id}_${signal.max_leverage}_${signal.level_risk}risk`" :message="riskTooltipMessage">
              <span
                  :class="[
                  'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border',
                  riskChipClass,
                ]"
              >
                <BoltIcon class="w-3" />
                {{ riskLevelText }}
              </span>
            </TheTooltip>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2">
          <template v-if="signal.locked">
            <TheTooltip :id="`${signal.id}_${signal.max_leverage}_lock`" :message="lockedMessage">
              <LockClosedIcon class="w-5 opacity-40" />
            </TheTooltip>
          </template>

          <template v-else>
            <Switch
                :disabled="idState.isChecking"
                @click.prevent="onToggleClick"
                :aria-busy="idState.isChecking ? 'true' : 'false'"
                :class="[
                isEnabled ? theme.switchBgActive : theme.switchBgInactive,
                idState.isChecking ? 'opacity-50 pointer-events-none' : '',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2',
                theme.switchRingFocus
              ]"
            >
              <span class="sr-only">
                {{ isEnabled ? $t('disable') : $t('enable') }}
              </span>

              <span
                  aria-hidden="true"
                  :class="[
                  isEnabled ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                ]"
              />
            </Switch>

            <div class="flex items-center font-normal text-sm">
              <Menu as="div" class="relative inline-block text-left">
                <div>
                  <MenuButton class="flex items-center rounded-full focus:outline-none p-1" :class="[$theme.baseBg, $theme.baseTextColor]">
                    <span class="sr-only">Open options</span>
                    <EllipsisVerticalIcon class="h-5 w-5 rounded-full" aria-hidden="true" />
                  </MenuButton>
                </div>

                <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                      class="absolute right-0 z-10 w-52 mt-1 max-w-sm flex-auto rounded-xl text-sm leading-6 shadow-lg ring-1 border ring-gray-900/5"
                      :class="[$theme.baseBg, $theme.baseTextColor, $theme.borderOrange]"
                  >
                    <div class="py-1">
                      <MenuItem>
                        <a @click="$router.push(`/signals/${signal.id}`)" href="javascript:void(0)" :class="['opacity-90', 'block px-4 py-2 text-sm']">
                          {{ $t('details') }}
                        </a>
                      </MenuItem>

                      <MenuItem v-if="showSettings">
                        <a @click="$router.push(`/signals/${signal.id}/settings`)" href="javascript:void(0)" :class="['opacity-90', 'block px-4 py-2 text-sm']">
                          {{ $t('settings') }}
                        </a>
                      </MenuItem>

                      <MenuItem v-if="isEnabled">
                        <a @click.prevent="openSelectModal()" href="javascript:void(0)" :class="['opacity-90', 'block px-4 py-2 text-sm']">
                          {{ $t('edit_exchanges') }}
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </template>
        </div>
      </header>

      <!-- Footer -->
      <div class="mt-3 flex items-center justify-between">
        <div class="flex items-center" :class="[signal.locked ? 'opacity-70' : '']">
          <template v-for="(allow_exchange, aeIdx) in signal.exchanges" :key="aeIdx">
            <TheTooltip :id="`${allow_exchange}_${signal.id}`" :message="capitalizeFirstLetter(allow_exchange)">
              <img
                  :src="`/img/${allow_exchange}.png`"
                  :alt="allow_exchange"
                  class="w-6 h-6 rounded-full ml-1 transition-opacity"
                  :class="iconOpacityClass(allow_exchange)"
              />
            </TheTooltip>
          </template>
        </div>

        <div v-if="idState.isChecking" class="flex items-center gap-2 text-xs opacity-70">
          <svg class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
          <span>{{ $t('checking') }}</span>
        </div>
      </div>
    </div>

    <!-- Locked ribbon -->
    <div v-if="signal.locked" class="absolute inset-x-0 bottom-0 px-4 pb-3">
      <div class="rounded-xl border text-xs px-2.5 py-1.5 flex items-center gap-2 bg-white/60 dark:bg-neutral-900/60 backdrop-blur" :class="[theme.cardBorderColor]">
        <LockClosedIcon class="w-4 h-4 opacity-60" />
        <span v-html="lockedMessage" />
      </div>
    </div>
  </article>

  <!-- Модалка выбора бирж (ленивый рендер) -->
  <TransitionRoot v-if="idState.isSelectModal" as="template" :show="idState.isSelectModal">
    <Dialog class="relative z-50" @close="closeSelectModal">
      <div class="fixed inset-0 bg-gray-950 bg-opacity-45 transition-opacity" />
      <div class="fixed inset-0 z-50 flex items-end justify-center">
        <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-300"
            enter-from="translate-y-full opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transform transition ease-in-out duration-300"
            leave-from="translate-y-0 opacity-100"
            leave-to="translate-y-full opacity-0"
        >
          <DialogPanel class="w-full rounded-t-2xl px-4 pt-5 pb-4 text-left shadow-orange sm:p-6" :class="[$theme.baseBg, $theme.baseTextColor]">
            <h3 class="text-lg font-bold">{{ $t('select_exchange') }}</h3>
            <div :class="[$theme.baseTextColor]" class="opacity-50 mb-4 text-sm">
              {{ $t('sel_exch_en_signal') }}
            </div>

            <div v-if="idState.loadingExchanges" class="py-8">
              <div class="flex items-center justify-center gap-2 opacity-70 text-sm">
                <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                </svg>
                {{ $t('loading') }}...
              </div>
            </div>

            <div v-else class="space-y-2">
              <template v-if="idState.exchanges.length">
                <label
                    v-for="exchange in idState.exchanges"
                    :key="exchange.id"
                    class="flex items-start gap-3 p-3 rounded-xl transition-colors"
                    :class="[
                    $theme.cardBg,
                    exchange.ok ? 'cursor-pointer hover:ring-2' : 'opacity-60',
                    idState.selectedExchangeIds.includes(exchange.id) ? 'ring-2' : ''
                  ]"
                >
                  <input
                      type="checkbox"
                      class="mt-1 hidden"
                      :value="exchange.id"
                      v-model="idState.selectedExchangeIds"
                      :disabled="!exchange.ok"
                  >

                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span class="font-medium capitalize">{{ exchange.exchange_slug }}</span>

                      <span v-if="exchange.ok" class="text-xs px-2 py-0.5 rounded-full bg-green5e/15 text-green5e">
                        {{ $t('available') }}
                      </span>
                      <span v-else class="text-xs px-2 py-0.5 rounded-full bg-red/15 text-red">
                        {{ $t('insufficient') }}
                      </span>
                    </div>

                    <div class="text-xs opacity-80 grid grid-cols-2 gap-x-4 gap-y-1">
                      <div>
                        <span class="opacity-70">{{ $t('need') }}:</span>
                        <span class="font-mono ml-1">{{ fmt(exchange.required_balance) }} {{ idState.currency }}</span>
                      </div>
                      <div>
                        <span class="opacity-70">{{ $t('available') }}:</span>
                        <span class="font-mono ml-1">{{ fmt(exchange.available) }} {{ idState.currency }}</span>
                      </div>
                      <div>
                        <span class="opacity-70">{{ $t('already_reserved') }}:</span>
                        <span class="font-mono ml-1">{{ fmt(exchange.reserved_existing) }} {{ idState.currency }}</span>
                      </div>
                      <div>
                        <span class="opacity-70">{{ $t('will_be_reserved') }}:</span>
                        <span class="font-mono ml-1">{{ fmt(exchange.reserved_balance) }} {{ idState.currency }}</span>
                      </div>
                    </div>

                    <div class="text-[11px] mt-1 opacity-60">
                      {{ $t('leverage') }}: {{ exchange.leverage }}x,
                      IM: <span class="uppercase">{{ exchange.im_type }}</span>
                      {{ exchange.im_type === 'percent' ? (exchange.im_value + '%') : exchange.im_value }}
                    </div>
                  </div>

                  <div v-if="idState.selectedExchangeIds.includes(exchange.id)" class="shrink-0 ml-2">
                    <svg class="w-5 h-5 text-green5e" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </label>
              </template>

              <p v-else class="opacity-75 text-sm">{{ $t('no_exchanges_found') }}</p>
            </div>

            <div class="mt-5 sm:mt-6 space-y-3">
              <button
                  @click="confirmSelection"
                  :disabled="idState.saving || !idState.selectedExchangeIds.length"
                  class="w-full py-3.5 rounded-xl font-semibold text-sm transition-transform active:scale-95 shadow-sm"
                  :class="[
                  $theme.bgBtn,
                  $theme.whiteText,
                  (idState.saving || !idState.selectedExchangeIds.length) ? 'opacity-60 pointer-events-none' : ''
                ]"
              >
                <span v-if="idState.saving">{{ $t('please_wait') }}...</span>
                <span v-else>{{ $t('enable') }}</span>
              </button>

              <button
                  @click="closeSelectModal"
                  type="button"
                  class="flex w-full items-center justify-center rounded-xl px-3 py-3 font-semibold"
                  :class="[$theme.placeBg, $theme.cardLabelTextColor]"
              >
                {{ $t('cancel') }}
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Switch } from "@headlessui/vue";
import { BoltIcon, LockClosedIcon } from "@heroicons/vue/16/solid";
import TheTooltip from "@/components/elem/TheTooltip.vue";
import { EllipsisVerticalIcon } from "@heroicons/vue/20/solid";
import { Menu, MenuButton, MenuItem, MenuItems, Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";

import { IdState } from "vue-virtual-scroller";

export default {
  name: "SignalItemNew",

  mixins: [
    // ВАЖНО: компонент будет реюзаться скроллером, idState делает локальное состояние привязанным к signal.id
    IdState({
      idProp: (vm) => Number(vm.signal?.id),
    }),
  ],

  components: {
    EllipsisVerticalIcon,
    Switch,
    TheTooltip,
    BoltIcon,
    LockClosedIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
  },

  props: {
    signal: { type: Object, required: true },
    theme: { type: Object, required: true },
    isEnabled: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
    capitalizeFirstLetter: { type: Function, required: true },
    checkUrl: { type: String, default: "" },
    showSettings: { type: Boolean, default: true },
    selectedExchanges: { type: Array, default: () => [] },

    // map подключённых бирж (id -> slug), грузится один раз в родителе
    exchangeIdToSlug: { type: Object, default: () => ({}) },
  },

  emits: ["toggle", "error"],

  // Заменяет data(): локальное состояние теперь привязано к id (signal.id)
  idState() {
    return {
      isChecking: false,
      isSelectModal: false,

      loadingExchanges: false,
      exchanges: [],
      selectedExchangeIds: [],
      saving: false,
      currency: null,
    };
  },

  computed: {
    activeExchangeSlugSet() {
      if (!this.isEnabled) return new Set();

      const ids = (this.idState.selectedExchangeIds?.length
              ? this.idState.selectedExchangeIds
              : (this.selectedExchanges || [])
      ).map(Number);

      const set = new Set();
      for (const id of ids) {
        const slug = this.exchangeIdToSlug?.[id];
        if (slug) set.add(slug);
      }
      return set;
    },

    riskData() {
      const level = this.signal?.level_risk || "low";
      const map = {
        low:    { text: this.$t("low_risk"),    tooltip: this.$t("low_risk_desc") },
        medium: { text: this.$t("medium_risk"), tooltip: this.$t("medium_risk_desc") },
        high:   { text: this.$t("high_risk"),   tooltip: this.$t("high_risk_desc") },
      };
      return map[level] || map.low;
    },
    riskLevelText() { return this.riskData.text; },
    riskTooltipMessage() { return this.riskData.tooltip; },

    riskChipClass() {
      const byLevel = {
        low:    "text-green-700 dark:text-green-300 bg-green-50/80 dark:bg-green-950/30 border-green-200/70 dark:border-green-900/60",
        medium: "text-yellow-800 dark:text-yellow-300 bg-yellow-50/80 dark:bg-yellow-950/30 border-yellow-200/70 dark:border-yellow-900/60",
        high:   "text-red-700 dark:text-red-300 bg-red-50/80 dark:bg-red-950/30 border-red-200/70 dark:border-red-900/60",
      };
      if (this.signal?.level_risk === "high") return byLevel.high;
      if (this.signal?.level_risk === "medium") return byLevel.medium;
      return byLevel.low;
    },

    tariffNames() {
      const tariffs = this.signal?.tariffs || [];
      return tariffs.map((t) => ` - ${t.title}`).join("<br>");
    },
    lockedMessage() {
      return `${this.$t("trading_pair_available_in_tariffs")} ${this.tariffNames}`;
    },
  },

  methods: {
    iconOpacityClass(slug) {
      if (!this.activeExchangeSlugSet.size) return "opacity-40";
      return this.activeExchangeSlugSet.has(slug) ? "opacity-100" : "opacity-40";
    },

    fmt(n) {
      if (n === null || n === undefined || n === "") return "-";
      return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
    },

    async onToggleClick() {
      if (this.idState.isChecking) return;

      if (this.isEnabled) {
        this.$emit("toggle", Number(this.signal.id));
        return;
      }

      if (this.showSettings === false) {
        this.$emit("toggle", Number(this.signal.id));
        return;
      }

      await this.openSelectModal();
    },

    async openSelectModal() {
      this.idState.isSelectModal = true;
      this.idState.loadingExchanges = true;

      // берем сохраненные биржи для сигнала
      this.idState.selectedExchangeIds = (this.selectedExchanges || []).map(Number);

      try {
        const { data } = await this.$api.get(`/signals/${this.signal.id}/check`);
        const rows = data?.checked || [];
        this.idState.currency = data?.currency || this.signal?.quoted_currency;

        this.idState.exchanges = rows.map((r) => ({
          id: Number(r.exchange_id),
          exchange_slug: r.exchange_slug,
          ok: r.ok,
          reason: r.reason,
          required_balance: r.required_balance,
          reserved_existing: r.reserved_existing,
          reserved_balance: r.reserved_balance,
          available: r.available,
          leverage: r.leverage,
          im_type: r.im_type,
          im_value: r.im_value,
        }));

        if (!this.idState.selectedExchangeIds.length) {
          this.idState.selectedExchangeIds = this.idState.exchanges.filter((e) => e.ok).map((e) => Number(e.id));
        } else {
          const okSet = new Set(this.idState.exchanges.filter((e) => e.ok).map((e) => Number(e.id)));
          this.idState.selectedExchangeIds = this.idState.selectedExchangeIds.filter((id) => okSet.has(Number(id)));
        }
      } catch (e) {
        this.idState.exchanges = [];
      } finally {
        this.idState.loadingExchanges = false;
      }
    },

    closeSelectModal() {
      if (this.idState.saving) return;
      this.idState.isSelectModal = false;
    },

    async confirmSelection() {
      if (!this.idState.selectedExchangeIds.length) return;

      this.idState.saving = true;
      try {
        const url = this.checkUrl || `/signals/${this.signal.id}/check`;
        const params = { exchange_ids: this.idState.selectedExchangeIds.map(Number) };

        const { data } = await this.$api.get(url, { params });

        if (data?.success) {
          this.$emit("toggle", { id: Number(this.signal.id), exchange_ids: [...this.idState.selectedExchangeIds] });
          this.idState.isSelectModal = false;
        } else {
          const failing = (data?.checked || []).find((x) => x.ok === false);
          const msg =
              failing?.reason === "NO_WALLET"
                  ? this.$t?.("no_wallet")
                  : this.$t?.("not_enough_balance_to_enable");

          this.$emit("error", {
            message: msg,
            required_balance: failing?.required_balance,
            reserved_balance: failing?.reserved_balance,
            currency: data?.currency || this.signal?.quoted_currency,
            signal_id: Number(this.signal?.id),
            signal_name: this.signal?.name,
          });
        }
      } catch (e) {
        const msg = e?.response?.data?.message || this.$t?.("something_went_wrong") || "Произошла ошибка";
        this.$emit("error", { message: msg, signal_id: Number(this.signal?.id), signal_name: this.signal?.name });
      } finally {
        this.idState.saving = false;
      }
    },
  },
};
</script>
